// @core/stores/secret/SecretSecretPolicyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { secretApi } from '@core/api';
import type {
  CreatePolicyRequest,
  ListPoliciesResponse,
  SecretPolicy,
  UpdatePolicyRequest,
} from '@core/types/secret';
import type { PageInfo } from '@core/types/pagination';

class SecretSecretPolicyStore {
  // List state
  items: SecretPolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: SecretPolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, SecretPolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): SecretPolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: SecretPolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create access policy
   */
  async CreatePolicy(data: CreatePolicyRequest): Promise<SecretPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await secretApi.CreatePolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete access policy
   */
  async DeletePolicy(policy_name: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await secretApi.DeletePolicy(policy_name);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_name);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_name) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_name);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get access policy
   */
  async GetPolicy(policy_name: string): Promise<SecretPolicy> {
    // Check cache first
    const cached = this.cache.get(policy_name);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await secretApi.GetPolicy(policy_name);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List access policies
   */
  async ListPolicies(params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListPoliciesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.ListPolicies(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update access policy
   */
  async UpdatePolicy(policy_name: string, data: UpdatePolicyRequest): Promise<SecretPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await secretApi.UpdatePolicy(policy_name, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const secretSecretPolicyStore = new SecretSecretPolicyStore();
